<template>
  <div>
    <b-dropdown
      ref="columns-values-dropdown"
      aria-role="list"
      position="is-bottom-right"
      append-to-body
    >
      <template #trigger>
        <b-button icon-right="angle-down" class="button is-primary" rounded outlined>
          {{ $t("table-filter.filter-columns") }}
        </b-button>
      </template>

      <b-dropdown-item aria-role="listitem" class="columns-filter-new" custom>
        <b-field :label="$t('table-filter.new-column-name')">
          <b-select
            v-model="newFilterDTO.property"
            expanded
            :placeholder="$t('table-filter.new-column-name-placeholder')"
            @update:model-value="setChosenFilterField"
          >
            <template v-for="filterField in filterFields" :key="filterField.field">
              <option :value="filterField.field">
                {{ filterField.label }}
              </option>
            </template>
          </b-select>
        </b-field>
        <b-radio
          v-if="newFilterDTO.property"
          v-model="filterKind"
          class="filter-value-radio"
          native-value="null"
        >
          <b-field :label="$t('table-filter.null')"></b-field>
        </b-radio>

        <b-radio
          v-if="newFilterDTO.property"
          v-model="filterKind"
          class="filter-value-radio"
          native-value="value"
        >
          <b-field :label="$t('table-filter.value-equals')"></b-field>
        </b-radio>
        <b-input
          v-if="newFilterDTO.property && chosenFilterField.possibleSearchValues.length === 0"
          v-model="newFilterValue"
          :disabled="filterKind == 'null'"
          :placeholder="$t('table-filter.new-column-value-placeholder')"
        ></b-input>

        <b-select
          v-if="newFilterDTO.property && chosenFilterField.possibleSearchValues.length > 0"
          v-model="newFilterValue"
          placeholder="table-filter.new-column-value-placeholder"
        >
          <template
            v-for="searchValue in chosenFilterField.possibleSearchValues"
            :key="searchValue"
          >
            <option :value="searchValue">
              {{ $t(searchValue) }}
            </option>
          </template>
        </b-select>
        <br />

        <b-button
          :disabled="!newFilterDTO.property || (filterKind == 'value' && !newFilterValue)"
          type="is-info"
          @click="addActiveFilter"
          >{{ $t("table-filter.add-filter") }}
        </b-button>
      </b-dropdown-item>
    </b-dropdown>
    <b-field v-if="activeFilters.length > 0" grouped group-multiline>
      <div v-for="filter in activeFilters" :key="filter.field" class="control">
        <b-taglist attached class="filters-list">
          <b-tag type="is-dark">{{ filter.label }}</b-tag>
          <b-tag type="is-grey">
            <span v-if="filter.values.length == 0">
              {{ $t("table-filter.none") }}
              <b-tag
                type="is-grey"
                class="close"
                @click="removeFilterValue(filter.property, value)"
              >
                <b-icon icon="times" />
              </b-tag>
            </span>
            <span v-for="(value, i) in filter.values" :key="value">
              <span v-if="value.length > 1"> {{ $t(value) }} </span>
              <span v-else> {{ $t("table-filter.none") }} </span>
              <b-tag
                type="is-grey"
                class="close"
                @click="removeFilterValue(filter.property, value)"
              >
                <b-icon icon="times" />
              </b-tag>
              <span v-if="i < filter.values.length - 1"
                ><strong>{{ $t("table-filter.or") }}</strong></span
              >
            </span>
          </b-tag>
        </b-taglist>
      </div>
    </b-field>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { FilterDTO } from "@/model/bean/GeneratedDTOs"
import { FilterDTOWithLabel } from "../tables/FilterDTOWithLabel"
import Activable from "../utils/Activable"
import {
  BButton,
  BIcon,
  BDropdown,
  BDropdownItem,
  BInput,
  BSelect,
  BField,
  BRadio,
  BTag,
  BTaglist,
} from "buefy"

export class FilterField {
  field: string
  label: string
  possibleSearchValues: string[]

  constructor() {
    this.field = ""
    this.label = ""
    this.possibleSearchValues = []
  }
}

export default defineComponent({
  name: "FilterComponent",
  components: {
    BTaglist,
    BDropdownItem,
    BButton,
    BInput,
    BField,
    BSelect,
    BDropdown,
    BRadio,
    BIcon,
    BTag,
  },
  props: {
    filterFields: {
      type: Array as PropType<FilterField[]>,
      default: () => [],
    },
    initialFilters: {
      type: Array as PropType<FilterDTO[]>,
      default: () => [],
    },
  },

  emits: ["update-active-filters"],
  data() {
    return {
      newFilterDTO: new FilterDTO(),
      filterKind: "value",
      activeFilters: [] as Array<FilterDTO>,
      chosenFilterField: new FilterField(),
      newFilterValue: "",
    }
  },
  watch: {
    initialFilters(): void {
      if (this.activeFilters.length != this.initialFilters.length) {
        this.activeFilters = JSON.parse(JSON.stringify(this.initialFilters))
        this.$emit("update-active-filters", this.activeFilters)
      }
    },
    newFilterValue(newValue: string): void {
      this.newFilterDTO.values = [newValue]
    },
  },
  mounted(): void {
    this.activeFilters = JSON.parse(JSON.stringify(this.initialFilters))
    this.newFilterDTO.values = []
  },
  methods: {
    setChosenFilterField(): void {
      this.chosenFilterField =
        this.filterFields.find((ff) => ff.field === this.newFilterDTO.property) || new FilterField()
    },

    addActiveFilter(): void {
      if (
        this.newFilterDTO.property &&
        (this.filterKind == "null" ||
          (this.newFilterDTO.values.length > 0 && this.newFilterDTO.values[0]))
      ) {
        // check if there is an already existing filter or create a new one
        const existingFilter = this.activeFilters.find((filter) => {
          return filter.property == this.newFilterDTO.property
        })
        if (!existingFilter) {
          const newFilter = new FilterDTOWithLabel()
          newFilter.property = this.newFilterDTO.property
          newFilter.values = this.newFilterDTO.values
          newFilter.label = this.filterFields.filter(
            (col) => col.field == newFilter.property,
          )[0].label
          this.activeFilters.push(newFilter)
        } else {
          // Push empty string if filter was without any value
          if (existingFilter.values.length == 0) {
            existingFilter.values.push("")
          }

          if (this.filterKind == "value") {
            existingFilter.values.push(this.newFilterDTO.values[0].trim())
          } else {
            existingFilter.values.push("")
          }
        }

        // reset values and close dropdown
        this.newFilterDTO = new FilterDTO()
        this.newFilterValue = ""
        this.filterKind = "value"
        const dropdown = this.$refs["columns-values-dropdown"] as unknown as Activable
        dropdown.isActive = false
        // Warn that filters changed
        this.$emit("update-active-filters", this.activeFilters)
      }
    },

    removeFilterValue(property: string, value: string): void {
      const filter = this.activeFilters.filter((filter) => filter.property == property)
      if (filter.length > 0) {
        if (filter[0].values.length > 1) {
          // Only remove value but none filter, other values remaine
          filter[0].values = filter[0].values.filter((val) => val != value)
        } else {
          // Was last value, remove filter from list
          this.activeFilters = this.activeFilters.filter((filter) => filter.property != property)
        }
      }
      this.$forceUpdate()

      // Warn tableView that filters changed
      this.$emit("update-active-filters", this.activeFilters)
    },
  },
})
</script>

<style lang="scss" scoped>
$filter-new-dropdown-width: 300px;
$filter-columns-filter-width: 500px;

.columns-list {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: $filter-columns-filter-width !important;
  padding: 10px;
}

.columns-list-input {
  width: $filter-columns-filter-width - 150px;
}

.columns-filter-new {
  width: $filter-new-dropdown-width !important;
  padding: 10px;
}

.filters-list {
  height: 50px;

  .tag .close {
    margin-bottom: 0;
    vertical-align: middle;
  }
}

.close {
  padding-left: 0 !important;
  margin-left: 0 !important;
  padding-right: 3px !important;
  margin-right: 3px !important;
  cursor: pointer;

  :hover {
    color: $danger;
    font-weight: bolder;
  }
}

.filter-value-radio {
  padding-top: 5px;
  padding-bottom: 10px;
}
</style>
